import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Play, Clock, Search, FileOutput } from 'lucide-react';
import { useRef } from 'react';
import { track } from '@/lib/analytics/mixpanel';

const BENEFIT_TILES = [
  { icon: Clock, label: 'Minutes to first pass' },
  { icon: Search, label: 'Quote-level traceability' },
  { icon: FileOutput, label: 'Working draft exports' },
] as const;

export function HeroSection() {
  const navigate = useNavigate();
  const sectionRef = useRef<HTMLElement>(null);

  const scrollToDemo = () => {
    track('hero_try_demo_click');
    document.getElementById('interactive-demo')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleGetStarted = () => {
    track('hero_get_started_click');
    navigate('/auth');
  };

  return (
    <section
      ref={sectionRef}
      className="relative min-h-[70svh] lg:min-h-svh flex flex-col items-center justify-center"
      aria-labelledby="hero-heading"
    >
      {/* Subtle radial glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        aria-hidden="true"
        style={{
          background: 'radial-gradient(ellipse 60% 40% at 50% 40%, hsl(var(--primary) / 0.07) 0%, transparent 70%)',
        }}
      />

      {/* Fade to next section */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-b from-transparent to-background pointer-events-none" aria-hidden="true" />

      <div className="relative max-w-3xl mx-auto px-5 sm:px-6 pt-20 sm:pt-24 md:pt-28 lg:pt-32 pb-12 md:pb-16 lg:pb-20 text-center">

        {/* Eyebrow */}
        <p className="mb-5 lg:mb-6 text-[11px] font-medium uppercase tracking-[0.2em] text-muted-foreground/70 animate-hero-fade-up">
          AI Qualitative Research Co-pilot
        </p>

        {/* Headline */}
        <h1
          id="hero-heading"
          className="text-[2rem] sm:text-5xl md:text-6xl font-bold tracking-tight leading-[1.1] text-foreground animate-hero-fade-up"
        >
          Develop <span className="text-primary">defensible</span> qualitative research, faster.
        </h1>

        {/* Subhead */}
        <p
          className="mt-5 sm:mt-7 lg:mt-8 text-base sm:text-lg text-muted-foreground leading-relaxed mx-auto animate-hero-fade-up [animation-delay:100ms]"
          style={{ maxWidth: '52ch' }}
        >
          Verithos turns your sources into evidence-linked findings and working research drafts you can{' '}
          <span className="whitespace-nowrap">refine and export.</span>
        </p>

        {/* CTAs */}
        <div
          className="mt-7 sm:mt-9 lg:mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 animate-hero-fade-up [animation-delay:200ms]"
        >
          <Button
            size="lg"
            onClick={handleGetStarted}
            className="w-full sm:w-auto min-w-[180px] h-12 sm:h-13 text-sm sm:text-base font-medium gradient-primary shadow-lg hover:shadow-xl hover:shadow-primary/20 transition-all duration-300 group"
            aria-label="Start free — sign up for Verithos"
          >
            Start free
            <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-0.5 transition-transform" aria-hidden="true" />
          </Button>

          <button
            onClick={scrollToDemo}
            className="inline-flex items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Watch demo — try the interactive demo"
          >
            <Play className="h-3.5 w-3.5" aria-hidden="true" />
            Watch demo
          </button>
        </div>

        {/* Benefit tiles */}
        <div className="mt-7 lg:mt-9 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-3.5 max-w-xs sm:max-w-2xl mx-auto animate-hero-fade-up [animation-delay:300ms]">
          {BENEFIT_TILES.map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="flex items-center gap-2.5 sm:justify-center px-4 py-2.5 rounded-xl border border-border/60 bg-card/50 shadow-sm backdrop-blur-sm"
            >
              <Icon className="h-4 w-4 shrink-0 text-primary/70" aria-hidden="true" />
              <span className="text-[13px] font-medium text-foreground/90 whitespace-nowrap">{label}</span>
            </div>
          ))}
        </div>

        {/* Trust micro-copy */}
        <p
          className="mt-5 lg:mt-6 text-xs text-muted-foreground/70 animate-hero-fade-up [animation-delay:400ms]"
        >
          Free to start · No credit card · Works with transcripts &amp; PDFs
        </p>
      </div>
    </section>
  );
}
